import { Button } from '@/components/ui/button';
import React from 'react';
import { Check } from 'lucide-react';
import { badgeVariants } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { formatFileSize } from '../../../utils/dateUtils';
import { DocumentItem, getCategoryName } from './documentItem';

interface DocumentPickerListProps {
  documents: DocumentItem[];
  selectedDocumentId?: string;
  disabled?: boolean;
  onSelect: (doc: DocumentItem) => void;
}

const DocumentPickerList: React.FC<DocumentPickerListProps> = ({
  documents,
  selectedDocumentId,
  disabled = false,
  onSelect,
}) => (
  <ul className="flex flex-col gap-1">
    {documents.map((doc) => {
      const isSelected = doc.id === selectedDocumentId;
      const categoryName = getCategoryName(doc.category);
      const size = doc.size || doc.fileSize;

      return (
        <li key={doc.id}>
          <Button variant="ghost"
            type="button"
            onClick={() => onSelect(doc)}
            disabled={disabled}
            className={cn(
              'block h-auto w-full whitespace-normal rounded-[8px] border px-4 py-2 text-left transition-colors hover:bg-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
              isSelected ? 'border-primary bg-accent' : 'border-border',
            )}
          >
            <span className="flex items-center gap-2">
              <span className="revy-cjk text-sm font-medium leading-[1.5]">{doc.title}</span>
              {isSelected && <Check className="size-4 shrink-0 text-primary" strokeWidth={2} />}
            </span>
            {doc.fileName && doc.fileName !== doc.title && (
              <span className="revy-cjk block text-xs leading-[1.5] text-muted-foreground">
                {doc.fileName}
              </span>
            )}
            <span className="mt-1 flex flex-wrap items-center gap-1">
              {categoryName && (
                <span
                  className={cn(
                    badgeVariants({ variant: 'outline' }),
                    'revy-cjk h-5 border-border px-2 py-0 text-[0.7rem] font-normal',
                  )}
                >
                  {categoryName}
                </span>
              )}
              {size ? (
                <span className="revy-figure text-xs text-muted-foreground">
                  {formatFileSize(size)}
                </span>
              ) : null}
            </span>
          </Button>
        </li>
      );
    })}
  </ul>
);

export default DocumentPickerList;
